'use client';
import { useState } from 'react';
import { FaqItem } from '@/lib/seo-schema';

interface FaqSectionProps {
  faqs: FaqItem[];
  title?: string;
  subtitle?: string;
}

export default function FaqSection({
  faqs,
  title = 'Frequently Asked Questions',
  subtitle = 'Everything you need to know about our free PDF tools — no surprises, no hidden fees.',
}: FaqSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(0); // First item open by default

  if (!faqs || faqs.length === 0) return null;

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section className="faq-section" style={{ padding: '3.5rem 0', maxWidth: '850px', margin: '0 auto', width: '100%' }}>
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--text-color)', marginBottom: '0.5rem', letterSpacing: '-0.02em' }}>
          {title}
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '1.05rem', maxWidth: '650px', margin: '0 auto' }}>
          {subtitle}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              style={{
                backgroundColor: 'var(--surface-color, #ffffff)',
                border: '1px solid var(--border-color, #e2e8f0)',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                boxShadow: isOpen ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
              }}
            >
              <button
                type="button"
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '1.25rem 1.5rem',
                  background: 'none',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: isOpen ? 'var(--primary-color, #2563eb)' : 'var(--text-color, #0f172a)',
                }}
              >
                <span>{faq.question}</span>
                <span
                  style={{
                    fontSize: '1.4rem',
                    lineHeight: '1',
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease, color 0.2s ease',
                    color: isOpen ? 'var(--primary-color, #2563eb)' : 'var(--text-muted, #64748b)',
                    marginLeft: '1rem',
                    flexShrink: 0,
                  }}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <div
                  style={{
                    padding: '0 1.5rem 1.25rem 1.5rem',
                    color: 'var(--text-muted, #475569)',
                    fontSize: '1rem',
                    lineHeight: '1.65',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '1rem',
                  }}
                >
                  {faq.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
